import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles({
    customSelectContainer: {
        display: "flex",
        flexDirection: "column",
        position: "relative",
        width: "100%",
        "&$disabled": {
            opacity: 0.6,
            pointerEvents: "none",
        },
    },
    disabled: {},
    customSelectLabel: {
        marginBottom: "0.5rem",
        fontSize: "1rem",
        color: "#333",
        "& $required": {
            color: "#e74c3c",
            marginLeft: "0.25rem",
        },
    },
    required: {},
    customSelectInput: {
        width: "100%",
        padding: "10px",
        fontSize: "14px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        backgroundColor: "#fff",
        color: "#333",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        height: "52px",
    },
    customSelectArrow: {
        marginLeft: "0.5rem",
        fontSize: "14px",
        transform: "rotate(0deg)",
        transition: "transform 0.2s ease",
        "&$open": {
            transform: "rotate(90deg)",
        },
    },
    open: {},
    customSelectDropdownBackground: {
        position: "fixed",
        left: 0,
        top: 0,
        width: "100vw",
        height: "100vh",
    },
    customSelectDropdown: {
        padding: 0,
        position: "absolute",
        top: "calc(100% + 0.25rem)",
        left: 0,
        right: 0,
        backgroundColor: "#fff",
        border: "1px solid #ccc",
        borderRadius: "4px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        maxHeight: "200px",
        overflowY: "auto",
        zIndex: 1000,
    },
    customSelectOption: {
        listStyle: "none",
        padding: "0.5rem",
        fontSize: "14px",
        cursor: "pointer",
        transition: "background-color 200ms",
        "&:hover": {
            backgroundColor: "#f0f0f0",
        },
        "&$selected": {
            backgroundColor: "#451E7B",
            color: "#fff",
        },
    },
    selected: {},
});

const Select = ({
    value, // Must be an array if component works in "multiple" mode and anything else (string, number...) if using "single" mode
    onChange, // Uses directly value of component instead of "e.target.value"
    onFocus,
    onBlur,
    optionValues = [], // Values, that will be the first argument in onChange(value) function instead of "e.target.value"
    optionLabels = [], // Labels, that will render in dropdown list. length of optionValues and optionLabels must be the same
    label,
    placeholder,
    helperText,
    style,
    inputStyle,
    labelStyle,
    disabled = false,
    isRequired = false, // Has a visual effect only on label and NOT on placeholder
    multiple = false, // Enables multi-select feature if true. NOTE: value should be an array in that case
}) => {
    const classes = useStyles(); // Инициализация стилей
    const [isOpen, setIsOpen] = useState(false);
    const toggleDropdown = () => {
        if (!disabled) setIsOpen(!isOpen);
    };

    const handleOptionClick = (optionValue) => {
        if (multiple) {
            const newValues = value.includes(optionValue)
                ? value.filter((val) => val !== optionValue)
                : [...value, optionValue];
            onChange(newValues);
        } else {
            onChange(optionValue);
            setIsOpen(false);
        }
    };

    const isSelected = (optionValue) => (multiple ? value.includes(optionValue) : value === optionValue);

    return (
        <div className={`${classes.customSelectContainer} ${disabled ? classes.disabled : ""}`} style={style}>
            {label && (
                <label className={classes.customSelectLabel} style={labelStyle}>
                    {label}
                    {isRequired && <span className={classes.required}>*</span>}
                </label>
            )}
            <div
                className={classes.customSelectInput}
                style={inputStyle}
                onClick={toggleDropdown}
                onFocus={onFocus}
                onBlur={onBlur}
                tabIndex={0}
            >
                {multiple
                    ? value.length > 0
                        ? value.map((val) => optionLabels[optionValues.indexOf(val)]).join(", ")
                        : placeholder
                    : value
                    ? optionLabels[optionValues.indexOf(value)] || optionLabels[optionLabels.indexOf(value)]
                    : placeholder}
                <span className={`${classes.customSelectArrow} ${isOpen ? classes.open : ""}`}>
                    <svg
                        stroke="currentColor"
                        fill="none"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        height="1em"
                        width="1em"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </span>
            </div>
            {isOpen && (
                <>
                    <div
                        className={classes.customSelectDropdownBackground}
                        onClick={() => setIsOpen(false)}
                    ></div>
                    <ul className={classes.customSelectDropdown}>
                        {optionValues.map((optionValue, index) => (
                            <li
                                key={optionValue}
                                className={`${classes.customSelectOption} ${
                                    isSelected(optionValue) ? classes.selected : ""
                                }`}
                                onClick={() => handleOptionClick(optionValue)}
                            >
                                {optionLabels[index] || optionValue}
                            </li>
                        ))}
                    </ul>
                </>
            )}
            {helperText && <p style={{ color: "red" }}>{helperText}</p>}
        </div>
    );
};

Select.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    onChange: PropTypes.func,
    optionValues: PropTypes.arrayOf(PropTypes.string).isRequired,
    optionLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    inputStyle: PropTypes.object,
    labelStyle: PropTypes.object,
    disabled: PropTypes.bool,
    isRequired: PropTypes.bool,
    multiple: PropTypes.bool,
};

export default Select;
